import React from "react";
import { defineMessages, useIntl } from "react-intl";
import { Table, TableCell } from "semantic-ui-react";
import { RatioCell } from "../RatioCell";
import { SalesReportData } from "../../hooks/useSalesReportData";
import { FormattedAmount } from "../FormattedAmount";
import { AmountCell } from "../AmountCell";

const m = defineMessages({
    previous: { id: "OrdersReportTable.ClientRows.Previous", defaultMessage: "Previous" },
    current: { id: "OrdersReportTable.ClientRows.Current", defaultMessage: "Current" },
    onOrder: { id: "OrdersReportTable.ClientRows.OnOrder", defaultMessage: "On order: " },
    billed: { id: "OrdersReportTable.ClientRows.Billed", defaultMessage: "Billed: " },
    total: { id: "OrdersReportTable.ClientRows.Total", defaultMessage: "Total" },
});

interface TotalRowsProps {
    salesReportData: SalesReportData;
}

export const SalesTotalRows: React.FC<TotalRowsProps> = ({ salesReportData }) => {
    const { formatMessage } = useIntl();

    const monthlyOrders = salesReportData.totalMonthlyOrders;

    const firstRowTopBorderStyle = {
        borderTopWidth: "medium",
        borderTopColor: "#404040",
    };

    const currentPeriodRow = (
        <Table.Row>
            <TableCell rowSpan={2} style={{ ...firstRowTopBorderStyle, verticalAlign: "baseline" }}>
                <div>{formatMessage(m.total)}</div>
            </TableCell>
            <TableCell style={{ ...firstRowTopBorderStyle, whiteSpace: "nowrap" }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <div>{formatMessage(m.onOrder)}</div>
                    <div>
                        <FormattedAmount amount={salesReportData.totalIncomingBillingAmount} />
                    </div>
                </div>
            </TableCell>
            <TableCell style={{ ...firstRowTopBorderStyle }}>{formatMessage(m.current)}</TableCell>
            {salesReportData.monthsInOrder.map((month) => (
                <AmountCell
                    key={month}
                    amount={monthlyOrders?.statsByMonth[month]?.currentPeriodTotal}
                    style={{ ...firstRowTopBorderStyle }}
                />
            ))}
            <AmountCell
                amount={monthlyOrders?.currentPeriodTotal}
                style={{ fontWeight: "bold", ...firstRowTopBorderStyle }}
            />
        </Table.Row>
    );

    const previousPeriodRow = (
        <Table.Row style={{ borderTop: "none" }}>
            <TableCell style={{ borderTop: "none", whiteSpace: "nowrap" }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <div>{formatMessage(m.billed)}</div>
                    <div>
                        <FormattedAmount amount={salesReportData.totalBilledAmountAfterEndDate} />
                    </div>
                </div>
            </TableCell>
            <TableCell style={{ borderTop: "none" }}>{formatMessage(m.previous)}</TableCell>
            {salesReportData.monthsInOrder.map((month) => (
                <AmountCell
                    key={month}
                    amount={monthlyOrders?.statsByMonth[month]?.previousPeriodTotal}
                    style={{ borderTop: "none" }}
                />
            ))}
            <AmountCell
                amount={monthlyOrders?.previousPeriodTotal}
                style={{ fontWeight: "bold" }}
            />
        </Table.Row>
    );

    const ratiosRow = (
        <Table.Row style={{ borderTop: "none" }}>
            <TableCell style={{ borderTop: "none" }}></TableCell>
            <TableCell style={{ borderTop: "none" }}></TableCell>
            <TableCell style={{ borderTop: "none", whiteSpace: "nowrap" }}></TableCell>
            {salesReportData.monthsInOrder.map((month) => (
                <RatioCell
                    key={month}
                    style={{ borderTop: "none" }}
                    ratio={monthlyOrders?.statsByMonth[month]?.ratio}
                    previousAmount={monthlyOrders?.statsByMonth[month]?.previousPeriodTotal}
                    currentAmount={monthlyOrders?.statsByMonth[month]?.currentPeriodTotal}
                />
            ))}
            <RatioCell
                style={{ borderTop: "none" }}
                ratio={monthlyOrders?.ratio}
                previousAmount={monthlyOrders?.previousPeriodTotal}
                currentAmount={monthlyOrders?.currentPeriodTotal}
            />
        </Table.Row>
    );

    return (
        <>
            {currentPeriodRow}
            {previousPeriodRow}
            {ratiosRow}
        </>
    );
};
